import { call, put, takeLatest } from 'redux-saga/effects';
import { request } from 'utils/request';
import { emailCreateEditActions } from './slice';
import { emailsGridActions } from 'app/pages/EmailsGridPage/slice';
import { notificationAlertActions } from 'app/pages/NotificationAlert/slice';
import { header } from 'utils/auth';

function getApiErrorMessage(error: any, fallback: string): string {
  const responseData =
    error?.response?.data ??
    error?.data ??
    error?.response ??
    error;

  const candidates: Array<unknown> = [
    error?.message,
    responseData?.message,
    responseData?.myAutoIQ_error_message,
    responseData?.statusText,
    responseData?.errorMessage,
  ];

  const msg = candidates.find(
    c => typeof c === 'string' && c.trim().length > 0,
  ) as string | undefined;

  if (msg) return msg;

  try {
    return JSON.stringify(responseData, null, 2);
  } catch {
    return fallback;
  }
}

export function* createEmailTemplate({ payload }: ReturnType<any>) {
  try {
    const requestURL = `/api/v2/templates?dealer=${payload.dealerId}`;
    const response = yield call(request, requestURL, {
      method: 'POST',
      headers: new Headers({
        'Content-Type': 'application/json',
        Authorization: header(),
      }),
      body: JSON.stringify(payload.data)
    });

    if (response.statusCode === 500) {
      yield put(emailCreateEditActions.requestCreateEmailTemplateError(response));

      return yield put(
        notificationAlertActions.addNotification({
          open: true,
          severity: 'error',
          message: getApiErrorMessage(response, 'Email template creation failed'),
        }),
      );
    }

    yield put(emailCreateEditActions.requestCreateEmailTemplateSuccess(response));

    // Ensure All Email Templates grid refreshes so the new template is visible
    yield put(emailsGridActions.forceRefreshEmailTemplates());

    if (payload.history) {
      payload.history.push("/emails-grid");
    }
    return yield put(
      notificationAlertActions.addNotification({
        open: true,
        severity: 'success',
        message: 'Email template saved',
      }),
    );
  } catch (err: any) {
    const errorPayload = err?.response?.data ?? err?.response ?? err;
    yield put(emailCreateEditActions.requestCreateEmailTemplateError(errorPayload));
    return yield put(
      notificationAlertActions.addNotification({
        open: true,
        severity: 'error',
        message: getApiErrorMessage(err, 'Email template creation failed'),
      }),
    );
  }
}

export function* getSavedEmailTemplate({ payload }: ReturnType<any>) {
  try {
    const requestURL = `/api/v2/templates/${payload.emailTemplateId}?dealer=${payload.dealerId}`;
    const response = yield call(request, requestURL, {
      method: 'GET',
      headers: new Headers({
        'Content-Type': 'application/json',
        Authorization: header(),
      }),
    });

    if (response.statusCode === 500) {
      yield put(emailCreateEditActions.requestSavedEmailTemplateError(response));

      return yield put(
        notificationAlertActions.addNotification({
          open: true,
          severity: 'error',
          message: getApiErrorMessage(
            response,
            'Email template fetch failed',
          ),
        }),
      );
    }
    yield put(emailCreateEditActions.requestSavedEmailTemplateSuccess(response));
  } catch (err: any) {
    const errorPayload = err?.response?.data ?? err?.response ?? err;
    yield put(emailCreateEditActions.requestSavedEmailTemplateError(errorPayload));
    return yield put(
      notificationAlertActions.addNotification({
        open: true,
        severity: 'error',
        message: getApiErrorMessage(err, 'Email template fetch failed'),
      }),
    );
  }
}

export function* updateEmailTemplate({ payload }: ReturnType<any>) {
  try {
    const requestURL = `/api/v2/templates/${payload.emailTemplateId}?dealer=${payload.dealerId}`;
    const response = yield call(request, requestURL, {
      method: 'PUT',
      headers: new Headers({
        'Content-Type': 'application/json',
        Authorization: header(),
      }),
      body: JSON.stringify(payload.data)
    });

    if (response.statusCode === 500) {
      yield put(emailCreateEditActions.requestUpdateEmailTemplateError(response));

      return yield put(
        notificationAlertActions.addNotification({
          open: true,
          severity: 'error',
          message: getApiErrorMessage(response, 'Email template update failed'),
        }),
      );
    }

    yield put(emailCreateEditActions.requestUpdateEmailTemplateSuccess(response));

    // Ensure All Email Templates grid refreshes so the updated template is visible
    yield put(emailsGridActions.forceRefreshEmailTemplates());

    if (payload.history) {
      payload.history.push("/emails-grid");
    }
    return yield put(
      notificationAlertActions.addNotification({
        open: true,
        severity: 'success',
        message: 'Email template saved',
      }),
    );
  } catch (err: any) {
    const errorPayload = err?.response?.data ?? err?.response ?? err;
    yield put(emailCreateEditActions.requestUpdateEmailTemplateError(errorPayload));
    return yield put(
      notificationAlertActions.addNotification({
        open: true,
        severity: 'error',
        message: getApiErrorMessage(err, 'Email template update failed'),
      }),
    );
  }
}

export function* uploadDataDetailsSaga() {
  yield takeLatest(
    emailCreateEditActions.requestCreateEmailTemplate.type,
    createEmailTemplate,
  );
  yield takeLatest(
    emailCreateEditActions.requestSavedEmailTemplate.type,
    getSavedEmailTemplate,
  );
  yield takeLatest(
    emailCreateEditActions.requestUpdateEmailTemplate.type,
    updateEmailTemplate,
  );
}
